// Medical Scores Database - Complete Collection Part 7
// Additional specialized scores

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  Beaker,
  User,
  Target,
  Shield,
  Zap,
  Eye
} from 'lucide-react';

export const SCORES_PART_7 = {
  // TIMI STEMI
  timiStemi: {
    name: 'Score TIMI STEMI',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Mortalité à 30j infarctus STEMI',
    color: 'rose',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '< 65 ans (0 pt)' },
        { value: 2, label: '65-74 ans (2 pts)' },
        { value: 3, label: '≥ 75 ans (3 pts)' }
      ]},
      { id: 'dm_htn_angina', label: 'Diabète, HTA ou angor', type: 'checkbox', points: 1 },
      { id: 'sbp', label: 'PAS < 100 mmHg', type: 'checkbox', points: 3 },
      { id: 'hr', label: 'FC > 100/min', type: 'checkbox', points: 2 },
      { id: 'killip', label: 'Killip II-IV', type: 'checkbox', points: 2 },
      { id: 'weight', label: 'Poids < 67 kg', type: 'checkbox', points: 1 },
      { id: 'anterior', label: 'STélévation antérieure ou BBG', type: 'checkbox', points: 1 },
      { id: 'time', label: 'Délai reperfusion > 4h', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = values.age || 0;
      if (values.dm_htn_angina) total += 1;
      if (values.sbp) total += 3;
      if (values.hr) total += 2;
      if (values.killip) total += 2;
      if (values.weight) total += 1;
      if (values.anterior) total += 1;
      if (values.time) total += 1;
      return { score: total, max: 14 };
    },
    interpret: (score) => {
      const mortality = {
        0: 0.8, 1: 1.6, 2: 2.2, 3: 4.4, 4: 7.3,
        5: 12.4, 6: 16.1, 7: 23.4, 8: 26.8
      };
      const mort = mortality[Math.min(score, 8)] || '>30';
      if (score <= 2) return { level: 'success', text: `TIMI STEMI ${score}`, detail: `Mortalité 30j: ~${mort}%` };
      if (score <= 4) return { level: 'warning', text: `TIMI STEMI ${score}`, detail: `Mortalité 30j: ~${mort}%` };
      return { level: 'danger', text: `TIMI STEMI ${score}`, detail: `Mortalité 30j: ~${mort}% - Haut risque` };
    },
    formula: 'TIMI STEMI: Âge + FR + Hémodynamique + ECG + Délai'
  },

  // TIMI NSTEMI (complémentaire)
  timiNstemi: {
    name: 'Score TIMI NSTEMI',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Risque à 14j SCA non ST+',
    color: 'rose',
    fields: [
      { id: 'age', label: 'Âge ≥ 65 ans', type: 'checkbox', points: 1 },
      { id: 'cad', label: '≥3 FR cardiovasculaires', type: 'checkbox', points: 1 },
      { id: 'coronary', label: 'Sténose coronaire ≥50% connue', type: 'checkbox', points: 1 },
      { id: 'stDeviation', label: 'Déviation ST ≥ 0.5mm', type: 'checkbox', points: 1 },
      { id: 'events', label: '≥2 événements angineux < 24h', type: 'checkbox', points: 1 },
      { id: 'aspirin', label: 'Aspirine < 7 jours', type: 'checkbox', points: 1 },
      { id: 'troponin', label: 'Troponine élevée', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 7 };
    },
    interpret: (score) => {
      const risk = { 0: 4.7, 1: 4.7, 2: 8.3, 3: 13.2, 4: 19.9, 5: 26.2, 6: 40.9, 7: 40.9 };
      if (score <= 2) return { level: 'success', text: `TIMI NSTEMI ${score}`, detail: `Risque 14j: ~${risk[score]}% - Faible` };
      if (score <= 4) return { level: 'warning', text: `TIMI NSTEMI ${score}`, detail: `Risque 14j: ~${risk[score]}% - Intermédiaire` };
      return { level: 'danger', text: `TIMI NSTEMI ${score}`, detail: `Risque 14j: ~${risk[score]}% - Stratégie invasive précoce` };
    },
    formula: 'TIMI NSTEMI: 7 critères à 1pt chacun'
  },

  // VARC-2 - Complications aortiques
  varc2: {
    name: 'Critères VARC-2',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Complications TAVI',
    color: 'rose',
    fields: [
      { id: 'mortality', label: 'Mortalité', type: 'select', options: [
        { value: 0, label: 'Non' },
        { value: 1, label: 'Oui' }
      ]},
      { id: 'mi', label: 'Infarctus du myocarde', type: 'checkbox', points: 1 },
      { id: 'stroke', label: 'AVC', type: 'select', options: [
        { value: 0, label: 'Non' },
        { value: 1, label: 'AVC mineur' },
        { value: 2, label: 'AVC majeur' }
      ]},
      { id: 'bleeding', label: 'Saignement', type: 'select', options: [
        { value: 0, label: 'Non' },
        { value: 1, label: 'Mineur' },
        { value: 2, label: 'Majeur' },
        { value: 3, label: 'Menace vitale' }
      ]},
      { id: 'aki', label: 'Insuffisance rénale aigue', type: 'select', options: [
        { value: 0, label: 'Non' },
        { value: 1, label: 'Stade 1' },
        { value: 2, label: 'Stade 2' },
        { value: 3, label: 'Stade 3' }
      ]},
      { id: 'vascularComp', label: 'Complication vasculaire', type: 'select', options: [
        { value: 0, label: 'Non' },
        { value: 1, label: 'Mineure' },
        { value: 2, label: 'Majeure' }
      ]},
      { id: 'pacemaker', label: 'Implantation pacemaker', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = (values.mortality || 0) + (values.mi ? 1 : 0) + (values.stroke || 0) + 
                  (values.bleeding || 0) + (values.aki || 0) + (values.vascularComp || 0) + 
                  (values.pacemaker ? 1 : 0);
      return { score: total, max: 13 };
    },
    interpret: (score, calculated, values) => {
      if (values.mortality === 1) return { level: 'danger', text: 'VARC-2: Décès', detail: 'Échec procédural' };
      if (score === 0) return { level: 'success', text: 'VARC-2: Pas de complication', detail: 'Succès procédural' };
      return { level: 'warning', text: `VARC-2: ${score} complication(s)`, detail: 'Voir détail des complications' };
    },
    formula: 'VARC-2: classification complications post-TAVI'
  },

  // Vesikari - Diarrhée enfant
  vesikari: {
    name: 'Score de Vésikari',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Sévérité gastro-entérite aiguë',
    color: 'violet',
    fields: [
      { id: 'diarrheaDuration', label: 'Durée diarrhée (jours)', type: 'select', options: [
        { value: 1, label: '1-4 jours (1 pt)' },
        { value: 2, label: '5 jours (2 pts)' },
        { value: 3, label: '≥ 6 jours (3 pts)' }
      ]},
      { id: 'diarrheaMax', label: 'Selles max/24h', type: 'select', options: [
        { value: 1, label: '1-3 (1 pt)' },
        { value: 2, label: '4-5 (2 pts)' },
        { value: 3, label: '≥ 6 (3 pts)' }
      ]},
      { id: 'vomitingDuration', label: 'Durée vomissements (jours)', type: 'select', options: [
        { value: 0, label: '0 jour (0 pt)' },
        { value: 1, label: '1 jour (1 pt)' },
        { value: 2, label: '2 jours (2 pts)' },
        { value: 3, label: '≥ 3 jours (3 pts)' }
      ]},
      { id: 'vomitingMax', label: 'Vomissements max/24h', type: 'select', options: [
        { value: 0, label: '0 (0 pt)' },
        { value: 1, label: '1 (1 pt)' },
        { value: 2, label: '2-4 (2 pts)' },
        { value: 3, label: '≥ 5 (3 pts)' }
      ]},
      { id: 'fever', label: 'Température', type: 'select', options: [
        { value: 0, label: '< 37.1°C (0 pt)' },
        { value: 1, label: '37.1-38.4°C (1 pt)' },
        { value: 2, label: '38.5-38.9°C (2 pts)' },
        { value: 3, label: '≥ 39°C (3 pts)' }
      ]},
      { id: 'dehydration', label: 'Déshydratation', type: 'select', options: [
        { value: 0, label: 'Absente (0 pt)' },
        { value: 2, label: 'Légère (1-5%) (2 pts)' },
        { value: 3, label: 'Modérée/Sévère (≥6%) (3 pts)' }
      ]},
      { id: 'treatment', label: 'Traitement', type: 'select', options: [
        { value: 0, label: 'Aucun (0 pt)' },
        { value: 1, label: 'Réhydratation orale (1 pt)' },
        { value: 2, label: 'Réhydratation IV (2 pts)' },
        { value: 3, label: 'Hospitalisation (3 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 20 };
    },
    interpret: (score) => {
      if (score < 7) return { level: 'success', text: `Vésikari ${score}/20`, detail: 'GEA légère' };
      if (score < 11) return { level: 'warning', text: `Vésikari ${score}/20`, detail: 'GEA modérée' };
      return { level: 'danger', text: `Vésikari ${score}/20`, detail: 'GEA sévère - Hospitalisation à discuter' };
    },
    formula: 'Vésikari: 7 items (0-3). <7 léger, 7-10 modéré, ≥11 sévère'
  },

  // VIS Score - Vasoactifs réa pédiatrique
  vis: {
    name: 'Score VIS',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Support vasoactif en réanimation pédiatrique',
    color: 'violet',
    fields: [
      { id: 'dopamine', label: 'Dopamine (µg/kg/min)', type: 'number', min: 0, max: 50, step: 0.1 },
      { id: 'dobutamine', label: 'Dobutamine (µg/kg/min)', type: 'number', min: 0, max: 50, step: 0.1 },
      { id: 'epinephrine', label: 'Adrénaline (µg/kg/min)', type: 'number', min: 0, max: 2, step: 0.01 },
      { id: 'norepinephrine', label: 'Noradrénaline (µg/kg/min)', type: 'number', min: 0, max: 2, step: 0.01 },
      { id: 'milrinone', label: 'Milrinone (µg/kg/min)', type: 'number', min: 0, max: 2, step: 0.01 },
      { id: 'vasopressin', label: 'Vasopressine (U/kg/h)', type: 'number', min: 0, max: 0.2, step: 0.001 }
    ],
    calculate: (values) => {
      const dopa = values.dopamine || 0;
      const dobu = values.dobutamine || 0;
      const epi = (values.epinephrine || 0) * 100;
      const norepi = (values.norepinephrine || 0) * 100;
      const milri = (values.milrinone || 0) * 10;
      const vaso = (values.vasopressin || 0) * 10000;
      
      const vis = dopa + dobu + epi + norepi + milri + vaso;
      return { score: vis.toFixed(1), max: '' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 10) return { level: 'success', text: `VIS ${score}`, detail: 'Faible support vasoactif' };
      if (s < 20) return { level: 'warning', text: `VIS ${score}`, detail: 'Support modéré' };
      if (s < 40) return { level: 'danger', text: `VIS ${score}`, detail: 'Support élevé' };
      return { level: 'danger', text: `VIS ${score}`, detail: 'Support très élevé - Pronostic réservé' };
    },
    formula: 'VIS = Dopa + Dobu + 100×(Epi+Norepi) + 10×Milri + 10000×Vaso'
  },

  // VTE-BLEED - Hémorragie sous AC
  vteBleed: {
    name: 'Score VTE-BLEED',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Risque hémorragique sous AC pour MTEV',
    color: 'rose',
    fields: [
      { id: 'cancer', label: 'Cancer actif', type: 'checkbox', points: 2 },
      { id: 'male', label: 'Sexe masculin + HTA non contrôlée', type: 'checkbox', points: 1 },
      { id: 'anemia', label: 'Anémie', type: 'checkbox', points: 1.5 },
      { id: 'bleeding', label: 'ATCD hémorragie', type: 'checkbox', points: 1.5 },
      { id: 'renalDysfunction', label: 'Clairance < 60 mL/min', type: 'checkbox', points: 1.5 },
      { id: 'age', label: 'Âge ≥ 60 ans', type: 'checkbox', points: 1.5 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.cancer) total += 2;
      if (values.male) total += 1;
      if (values.anemia) total += 1.5;
      if (values.bleeding) total += 1.5;
      if (values.renalDysfunction) total += 1.5;
      if (values.age) total += 1.5;
      return { score: total, max: 9 };
    },
    interpret: (score) => {
      if (score < 2) return { level: 'success', text: `VTE-BLEED ${score}`, detail: 'Faible risque hémorragique - AC au long cours justifié' };
      return { level: 'danger', text: `VTE-BLEED ${score}`, detail: 'Élevé - Durée AC à discuter' };
    },
    formula: 'VTE-BLEED ≥ 2 = risque hémorragique élevé'
  },

  // Westley - Laryngite
  westley: {
    name: 'Score de Westley',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Sévérité laryngite aiguë',
    color: 'violet',
    fields: [
      { id: 'stridor', label: 'Stridor', type: 'select', options: [
        { value: 0, label: '0 - Absent' },
        { value: 1, label: '1 - Au repos, audible au stéthoscope' },
        { value: 2, label: '2 - Au repos, audible sans stéthoscope' }
      ]},
      { id: 'retraction', label: 'Tirage', type: 'select', options: [
        { value: 0, label: '0 - Absent' },
        { value: 1, label: '1 - Léger' },
        { value: 2, label: '2 - Modéré' },
        { value: 3, label: '3 - Sévère' }
      ]},
      { id: 'airEntry', label: 'Entrée d\'air', type: 'select', options: [
        { value: 0, label: '0 - Normale' },
        { value: 1, label: '1 - Diminuée' },
        { value: 2, label: '2 - Très diminuée' }
      ]},
      { id: 'cyanosis', label: 'Cyanose', type: 'select', options: [
        { value: 0, label: '0 - Absente' },
        { value: 4, label: '4 - À l\'agitation' },
        { value: 5, label: '5 - Au repos' }
      ]},
      { id: 'consciousness', label: 'Conscience', type: 'select', options: [
        { value: 0, label: '0 - Normale' },
        { value: 5, label: '5 - Altérée' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 17 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Westley ${score}`, detail: 'Laryngite légère - Corticoïdes' };
      if (score <= 7) return { level: 'warning', text: `Westley ${score}`, detail: 'Laryngite modérée - Corticoïdes + aérosol adrénaliné' };
      if (score <= 11) return { level: 'danger', text: `Westley ${score}`, detail: 'Laryngite sévère - Nébulisation adrénaline + surveillance' };
      return { level: 'danger', text: `Westley ${score}`, detail: 'Insuffisance respiratoire imminente - Intubation à envisager' };
    },
    formula: 'Westley: ≤2 léger, 3-7 modéré, 8-11 sévère, >11 insuffisance respiratoire'
  },

  // WETBPD - Ostéoporose cortico-induite
  glucocorticoidRisk: {
    name: 'Risque ostéoporose cortisonique',
    category: 'Rhumatologie',
    icon: Bone,
    description: 'Évaluation risque fracturaire sous corticoïdes',
    color: 'amber',
    fields: [
      { id: 'dose', label: 'Dose prédnisone équivalent', type: 'select', options: [
        { value: 0, label: '< 2.5 mg/j (faible)' },
        { value: 1, label: '2.5-7.5 mg/j (modéré)' },
        { value: 2, label: '> 7.5 mg/j (élevé)' }
      ]},
      { id: 'duration', label: 'Durée prévue', type: 'select', options: [
        { value: 0, label: '< 3 mois' },
        { value: 1, label: '≥ 3 mois' }
      ]},
      { id: 'age', label: 'Âge ≥ 65 ans', type: 'checkbox', points: 1 },
      { id: 'fracture', label: 'ATCD fracture ostéoporotique', type: 'checkbox', points: 2 },
      { id: 'tScore', label: 'T-score ≤ -2.5', type: 'checkbox', points: 2 },
      { id: 'menopause', label: 'Ménopause', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = (values.dose || 0) + (values.duration || 0);
      if (values.age) total += 1;
      if (values.fracture) total += 2;
      if (values.tScore) total += 2;
      if (values.menopause) total += 1;
      return { score: total, max: 9 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Risque faible (${score})`, detail: 'Calcium + Vitamine D, surveillance' };
      if (score <= 4) return { level: 'warning', text: `Risque modéré (${score})`, detail: 'DMO, Calcium + Vit D, traitement si T-score ≤-1.5' };
      return { level: 'danger', text: `Risque élevé (${score})`, detail: 'Traitement anti-ostéoporotique recommandé (bisphosphonates)' };
    },
    formula: 'Risque cortico-induit: dose + durée + FR'
  },

  // Wildman - SOFA modifié réa
  wildman: {
    name: 'Score de Wildman',
    category: 'Réanimation',
    icon: Activity,
    description: 'Pronostic BPCO en réanimation',
    color: 'red',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 40, max: 100 },
      { id: 'albumin', label: 'Albumine < 25 g/L', type: 'checkbox', points: 1 },
      { id: 'activities', label: 'Limitation activités pré-hospitalisation', type: 'checkbox', points: 1 },
      { id: 'ventilation', label: 'Ventilation mécanique', type: 'checkbox', points: 1 },
      { id: 'fev1', label: 'VEMS < 50% (si connu)', type: 'checkbox', points: 1 },
      { id: 'cor', label: 'Cœur pulmonaire', type: 'checkbox', points: 1 },
      { id: 'failure', label: 'Échec VNI', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      const age = values.age || 65;
      if (age >= 70) total += 1;
      if (values.albumin) total += 1;
      if (values.activities) total += 1;
      if (values.ventilation) total += 1;
      if (values.fev1) total += 1;
      if (values.cor) total += 1;
      if (values.failure) total += 1;
      return { score: total, max: 7 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Wildman ${score}`, detail: 'Bon pronostic - Mortalité < 15%' };
      if (score <= 4) return { level: 'warning', text: `Wildman ${score}`, detail: 'Pronostic réservé - Mortalité ~30%' };
      return { level: 'danger', text: `Wildman ${score}`, detail: 'Pronostic sombre - Mortalité > 50%' };
    },
    formula: 'Wildman: 7 critères pronostiques BPCO en réa'
  },

  // Yale Observation Scale - Fièvre enfant
  yos: {
    name: 'Yale Observation Scale',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Évaluation enfant fébrile (3-36 mois)',
    color: 'violet',
    fields: [
      { id: 'cry', label: 'Qualité des pleurs', type: 'select', options: [
        { value: 1, label: '1 - Vigoureux, normal' },
        { value: 3, label: '3 - Gémissement ou sanglot' },
        { value: 5, label: '5 - Faible ou aigu' }
      ]},
      { id: 'parentResponse', label: 'Réaction stimulation parent', type: 'select', options: [
        { value: 1, label: '1 - Pleure brièvement puis arrête, content' },
        { value: 3, label: '3 - Pleure par intermittence' },
        { value: 5, label: '5 - Pleure continuellement ou répond à peine' }
      ]},
      { id: 'stateVariation', label: 'Variation état conscience', type: 'select', options: [
        { value: 1, label: '1 - Réveillé ou se réveille vite' },
        { value: 3, label: '3 - Yeux se ferment brièvement, se réveille difficilement' },
        { value: 5, label: '5 - S\'endort, ne se réveille pas' }
      ]},
      { id: 'color', label: 'Couleur', type: 'select', options: [
        { value: 1, label: '1 - Rose' },
        { value: 3, label: '3 - Extrémités pâles ou acrocyanose' },
        { value: 5, label: '5 - Pâle, cyanose, marbré, gris' }
      ]},
      { id: 'hydration', label: 'Hydratation', type: 'select', options: [
        { value: 1, label: '1 - Peau, yeux, muqueuses normaux' },
        { value: 3, label: '3 - Bouche un peu sèche' },
        { value: 5, label: '5 - Peau pâteuse, muqueuses sèches, yeux creux' }
      ]},
      { id: 'socialResponse', label: 'Réponse sociale', type: 'select', options: [
        { value: 1, label: '1 - Sourit, alerte' },
        { value: 3, label: '3 - Sourire bref, peu alerte' },
        { value: 5, label: '5 - Ne sourit pas, anxieux, inexpressif' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 30 };
    },
    interpret: (score) => {
      if (score <= 10) return { level: 'success', text: `YOS ${score}/30`, detail: 'Faible risque infection grave (~2.7%)' };
      if (score <= 16) return { level: 'warning', text: `YOS ${score}/30`, detail: 'Risque modéré - Surveillance rapprochée' };
      return { level: 'danger', text: `YOS ${score}/30`, detail: 'Risque élevé infection grave (~26%) - Bilan septique' };
    },
    formula: 'YOS: 6 items (1-3-5). ≤10 faible risque, >16 haut risque'
  },

  // YKL-40 / YTHDF2 (placeholder for specialized markers)
  zung: {
    name: 'Échelle de Zung (SDS)',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Auto-évaluation dépression',
    color: 'indigo',
    fields: [
      { id: 'depressed', label: 'Je me sens triste et cafardeux', type: 'select', options: [
        { value: 1, label: '1 - Jamais/Rarement' }, { value: 2, label: '2 - Parfois' },
        { value: 3, label: '3 - Souvent' }, { value: 4, label: '4 - Presque toujours' }
      ]},
      { id: 'morningBest', label: 'Le matin est le moment où je me sens le mieux', type: 'select', options: [
        { value: 4, label: '1 - Jamais' }, { value: 3, label: '2 - Parfois' },
        { value: 2, label: '3 - Souvent' }, { value: 1, label: '4 - Presque toujours' }
      ]},
      { id: 'crying', label: 'J\'ai des crises de larmes ou envie de pleurer', type: 'select', options: [
        { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'sleep', label: 'J\'ai du mal à dormir la nuit', type: 'select', options: [
        { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'appetite', label: 'Je mange autant qu\'avant', type: 'select', options: [
        { value: 4, label: '1 - Jamais' }, { value: 3, label: '2' }, { value: 2, label: '3' }, { value: 1, label: '4 - Toujours' }
      ]},
      { id: 'sex', label: 'J\'ai encore du plaisir dans ma vie sexuelle', type: 'select', options: [
        { value: 4, label: '1 - Jamais' }, { value: 3, label: '2' }, { value: 2, label: '3' }, { value: 1, label: '4 - Toujours' }
      ]},
      { id: 'weight', label: 'Je remarque que je perds du poids', type: 'select', options: [
        { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'constipation', label: 'Je suis constipé', type: 'select', options: [
        { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'heart', label: 'Mon cœur bat plus vite que d\'habitude', type: 'select', options: [
        { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'tired', label: 'Je me fatigue sans raison', type: 'select', options: [
        { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      // Zung index = score brut / 80 * 100
      const index = (total / 40) * 100; // 10 items ici
      return { score: total, max: 40, index: index.toFixed(0) };
    },
    interpret: (score, calculated) => {
      const idx = parseInt(calculated?.index || 0);
      if (idx < 50) return { level: 'success', text: `Zung ${score} (idx ${idx})`, detail: 'Pas de dépression significative' };
      if (idx < 60) return { level: 'warning', text: `Zung ${score} (idx ${idx})`, detail: 'Dépression légère' };
      if (idx < 70) return { level: 'danger', text: `Zung ${score} (idx ${idx})`, detail: 'Dépression modérée' };
      return { level: 'danger', text: `Zung ${score} (idx ${idx})`, detail: 'Dépression sévère' };
    },
    formula: 'Zung SDS: Index = Score/80×100. <50 normal, 50-59 léger, 60-69 modéré, ≥70 sévère'
  }
};
